<script setup lang="ts">
import { computed, ref } from "vue";
import { rounds, summariseGames } from "../../domain/history.js";
import { replay } from "../../domain/rules.js";
import type { Game } from "../../domain/store.js";

/**
 * History, PRD FR-5.
 *
 * A list of finished games, newest first; opening one shows the final
 * standings and every turn grouped by round, with strikes marked so a run of
 * bad luck is visible at a glance.
 */

const props = defineProps<{
  title: string;
  games: Game[];
  /** Deleted but still inside the thirty days (8.3). */
  binned: Game[];
  names: Record<string, string>;
}>();

const emit = defineEmits<{
  back: [];
  remove: [gameId: string];
  restore: [gameId: string];
}>();

const openId = ref<string | null>(null);
const confirming = ref<string | null>(null);

const list = computed(() => summariseGames(props.games));
const open = computed(() => props.games.find((g) => g.id === openId.value) ?? null);
const openRounds = computed(() => (open.value ? rounds(open.value) : []));
const openStandings = computed(() =>
  open.value ? replay(open.value.order, open.value.turns).standings : [],
);

const nameOf = (id: string): string => props.names[id] ?? id;
const MEDALS = ["🥇", "🥈", "🥉"];

const date = (ms: number): string =>
  new Date(ms).toLocaleDateString("de-DE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
</script>

<template>
  <div class="screen">
    <header class="nav">
      <button
        class="nav-btn"
        @click="openId ? (openId = null) : emit('back')"
      >
        ‹ Zurück
      </button>
      <span class="title">{{ open ? date(open.finishedAt ?? open.startedAt) : title }}</span>
      <span class="spacer" />
    </header>

    <div class="scroll">
      <!-- List of finished games -->
      <template v-if="!open">
        <p
          v-if="list.length === 0"
          class="empty"
        >
          Noch keine beendeten Spiele.
        </p>
        <ul class="list">
          <li
            v-for="row in list"
            :key="row.game.id"
            class="row"
          >
            <button
              class="open"
              @click="openId = row.game.id"
            >
              <span class="line">
                <span class="winner">{{ row.winnerId ? nameOf(row.winnerId) : "—" }}</span>
                <span class="score num">{{ row.winningScore.toLocaleString("de-DE") }}</span>
              </span>
              <span class="meta">
                {{ date(row.at) }} · {{ row.players }} Spieler · {{ row.turns }} Züge
              </span>
            </button>
            <button
              class="del"
              :aria-label="`Spiel vom ${date(row.at)} löschen`"
              @click="confirming = row.game.id"
            >
              <svg
                class="ico"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="m7 7 10 10M17 7 7 17" />
              </svg>
            </button>
          </li>
        </ul>
      </template>

      <!-- 8.3: deleted games stay recoverable for thirty days. Without this
           the delete is final, because sync is one-way and there is nothing
           to fetch the game back from. -->
      <template v-if="!open && props.binned.length > 0">
        <h2 class="h2">
          Zuletzt gelöscht
        </h2>
        <ul class="list">
          <li
            v-for="row in summariseGames(props.binned)"
            :key="row.game.id"
            class="row"
          >
            <div class="open binned">
              <span class="line">
                <span class="winner">{{ row.winnerId ? nameOf(row.winnerId) : "—" }}</span>
                <span class="score num">{{ row.winningScore.toLocaleString("de-DE") }}</span>
              </span>
              <span class="meta">{{ date(row.at) }} · gelöscht</span>
            </div>
            <button
              class="restore"
              :aria-label="`Spiel vom ${date(row.at)} wiederherstellen`"
              @click="emit('restore', row.game.id)"
            >
              Zurück
            </button>
          </li>
        </ul>
      </template>

      <!-- One game in detail. An explicit condition, not v-else: the bin block
           above sits between this and the list, so a chain would pair it with
           the wrong branch. -->
      <template v-if="open">
        <ol class="standings">
          <li
            v-for="(p, i) in openStandings"
            :key="p.playerId"
            class="stand"
          >
            <span class="medal">{{ MEDALS[i] ?? i + 1 }}</span>
            <span class="sname">{{ nameOf(p.playerId) }}</span>
            <span class="stotal num">{{ p.total.toLocaleString("de-DE") }}</span>
          </li>
        </ol>

        <div
          v-for="r in openRounds"
          :key="r.number"
          class="round"
        >
          <div class="round-no">
            Runde {{ r.number }}
          </div>
          <div
            v-for="(e, i) in r.entries"
            :key="i"
            class="entry"
            :class="{ 'entry--strike': e.turn?.type === 'strike' }"
          >
            <span class="ename">{{ nameOf(e.playerId) }}</span>
            <span class="epoints num">
              {{ e.turn?.type === "strike" ? "Strich" : e.turn?.points.toLocaleString("de-DE") }}
            </span>
            <span class="etotal num">{{ e.total.toLocaleString("de-DE") }}</span>
          </div>
        </div>
      </template>
    </div>

    <!-- FR-5.3: deleting a game removes it from every statistic. -->
    <div
      v-if="confirming"
      class="sheet"
    >
      <div class="sheet-card">
        <p class="sheet-text">
          Spiel löschen? Es verschwindet damit auch aus allen Statistiken.
        </p>
        <div class="sheet-actions">
          <button
            class="secondary"
            @click="confirming = null"
          >
            Abbrechen
          </button>
          <button
            class="danger"
            @click="
              emit('remove', confirming);
              confirming = null;
            "
          >
            Löschen
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.screen {
  height: 100dvh;
  display: flex;
  flex-direction: column;
  padding-left: max(12px, var(--safe-left));
  padding-right: max(12px, var(--safe-right));
  padding-bottom: calc(var(--safe-bottom) + 8px);
}

.nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: calc(var(--safe-top) + 6px) 0 6px;
  min-height: 52px;
}

.nav-btn {
  background: none;
  border: none;
  color: var(--accent);
  font-size: 15px;
  padding: 8px 4px;
  min-height: 44px;
}

.title {
  font-size: 15px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spacer {
  width: 56px;
}

.scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.empty {
  color: var(--text-2);
  font-size: 14px;
  margin: 12px 4px;
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.row {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
}

.open {
  flex: 1;
  min-width: 0;
  text-align: left;
  border: none;
  background: var(--card);
  border-radius: var(--radius-card);
  padding: 10px 14px;
  min-height: 56px;
}

.line {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: baseline;
}

.winner {
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.score {
  font-size: 15px;
  font-weight: 600;
  color: var(--accent);
}

.meta {
  display: block;
  font-size: 12px;
  color: var(--text-2);
  margin-top: 1px;
}

.binned {
  opacity: 0.55;
}

.restore {
  border: none;
  background: var(--accent-tint);
  color: var(--accent);
  border-radius: var(--radius-card);
  padding: 0 14px;
  min-height: 56px;
  font-size: 14px;
  font-weight: 600;
}

.h2 {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-2);
  margin: 18px 4px 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.del {
  border: none;
  background: var(--card);
  border-radius: var(--radius-card);
  color: var(--text-3);
  width: 48px;
  display: grid;
  place-items: center;
}

.ico {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
}

.standings {
  list-style: none;
  margin: 0 0 14px;
  padding: 0;
}

.stand {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--card);
  border-radius: var(--radius-card);
  padding: 8px 12px;
  margin-bottom: 4px;
  min-height: 40px;
}

.medal {
  width: 1.5em;
  text-align: center;
  color: var(--text-3);
}

.sname {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 15px;
}

.stotal {
  font-size: 16px;
  font-weight: 600;
}

.round {
  margin-bottom: 10px;
}

.round-no {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-3);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 4px 3px;
}

.entry {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--card);
  border-radius: 10px;
  padding: 7px 12px;
  margin-bottom: 3px;
  min-height: 34px;
  font-size: 14px;
}

/* A strike is the thing people scan for, so it gets the colour and a rule. */
.entry--strike {
  color: var(--danger);
}

.entry--strike .epoints {
  font-weight: 600;
}

.ename {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.epoints {
  min-width: 4em;
  text-align: right;
}

.etotal {
  min-width: 4.2em;
  text-align: right;
  color: var(--text-2);
}

.sheet {
  position: fixed;
  inset: 0;
  background: rgb(0 0 0 / 0.35);
  display: flex;
  align-items: flex-end;
  z-index: 40;
  padding: 16px;
  padding-bottom: calc(16px + var(--safe-bottom));
}

.sheet-card {
  width: 100%;
  background: var(--card);
  border-radius: var(--radius-card);
  padding: 16px;
}

.sheet-text {
  margin: 0 0 12px;
  font-size: 15px;
}

.sheet-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.secondary,
.danger {
  border: none;
  border-radius: var(--radius-control);
  min-height: 48px;
  font-size: 16px;
  font-weight: 600;
}

.secondary {
  background: var(--bg);
  color: var(--accent);
}

.danger {
  background: var(--danger);
  color: #fff;
}
</style>
